import React, { useState } from 'react'
import { Plus, Loader2, AlertTriangle, Users } from 'lucide-react'
import Drawer from '@/components/ui/Drawer'
import { useToast } from '@/contexts/ToastContext'
import useScoutStore from '@/stores/scoutStore'
import useScoutOperationStore from '@/stores/scoutOperationStore'
import useGlobalCustomerStore from '@/stores/globalCustomerStore'

interface AddAccountDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const AddAccountDrawer: React.FC<AddAccountDrawerProps> = ({ isOpen, onClose }) => {
  const { showSuccess, showError } = useToast()
  const { createAccount, loadAccounts, accountLimits } = useScoutStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [username, setUsername] = useState('')
  const [platformId, setPlatformId] = useState<number>(1) // 1=Instagram, 2=TikTok, 11=Facebook
  const [isCreating, setIsCreating] = useState(false)

  const isFacebook = platformId === 11

  const maxAccounts = accountLimits?.maxAccounts ?? null
  const activeCount = accountLimits?.activeCount ?? 0
  const isNotConfigured = maxAccounts === 0
  const isAtLimit = maxAccounts !== null && maxAccounts > 0 && activeCount >= maxAccounts

  const resetForm = () => {
    setUsername('')
    setPlatformId(1)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username.trim()) {
      showError('Validation Error', 'Username is required')
      return
    }

    if (!selectedCustomer?.id) {
      showError('Error', 'No customer selected')
      return
    }

    const customerId = String(selectedCustomer.id)
    setIsCreating(true)

    try {
      const account = await createAccount(customerId, username.trim(), platformId)

      if (account.scanId) {
        useScoutOperationStore.getState().addOperation({
          scanId: account.scanId,
          username: account.username,
          customerId,
          startedAt: Date.now()
        })
      }

      const label = isFacebook ? account.username : `@${account.username}`
      showSuccess('Account Added', `${label} added. Initial analysis can take up to 15 minutes — you'll be notified when complete.`)
      await loadAccounts(customerId)
      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      if (errorMessage.includes('limit') || errorMessage.includes('403')) {
        showError('Account Limit Reached', errorMessage)
      } else if (errorMessage.includes('already')) {
        showError('Duplicate Account', errorMessage)
      } else {
        showError('Failed to Add Account', errorMessage)
      }
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} title="Add Scout Account" width="sm">
      {isNotConfigured ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-amber-800">Brand Scout accounts are not enabled for your organization.</p>
              <p className="mt-1 text-[10px] text-amber-700">Contact your account manager to enable Brand Scout account tracking.</p>
            </div>
          </div>
          <div className="flex pt-1 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {maxAccounts !== null && maxAccounts > 0 && (
            <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${isAtLimit ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
              {isAtLimit
                ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                : <Users className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              }
              <span className={`text-xs ${isAtLimit ? 'text-amber-800' : 'text-gray-600'}`}>
                {isAtLimit
                  ? <>You have reached the maximum of <span className="font-medium">{maxAccounts}</span> accounts ({activeCount} active). Disable an account or contact your account manager to increase your limit.</>
                  : <><span className="font-medium">{activeCount}</span> of <span className="font-medium">{maxAccounts}</span> accounts used</>
                }
              </span>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Platform
            </label>
            <select
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              value={platformId}
              onChange={(e) => setPlatformId(Number(e.target.value))}
              disabled={isCreating}
            >
              <option value={1}>Instagram</option>
              <option value={2}>TikTok</option>
              <option value={11}>Facebook</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {isFacebook ? 'Facebook Page (name or URL)' : 'Platform Username / Handle'} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              {!isFacebook && (
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
              )}
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(isFacebook ? e.target.value : e.target.value.replace('@', ''))}
                placeholder={isFacebook ? 'page name or facebook.com/page' : 'username'}
                className={`w-full ${isFacebook ? 'pl-2.5' : 'pl-7'} pr-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 transition-colors`}
                disabled={isCreating}
                autoFocus
              />
            </div>
            <p className="mt-1 text-[10px] text-gray-400">
              An initial scan of the last 30 days will start automatically. This can take up to 15 minutes.
            </p>
          </div>

          <div className="flex gap-3 pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !username.trim() || isAtLimit}
              className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add Account
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </Drawer>
  )
}

export default AddAccountDrawer
